import { ETH_MONTHS_EN, type EthDate, ethDaysInMonth } from '@assist/core';
import { useEffect, useState } from 'preact/hooks';

interface Props {
  id: string;
  value: EthDate | null;
  todayEth: EthDate;
  onChange: (v: EthDate | null) => void;
  /** How many years back to offer. */
  span?: number;
}

/** Day / month / year selects in the Ethiopian calendar. Only real dates can be chosen: Pagume
 * offers 5 or 6 days according to the year. A partly chosen date is kept on screen while the user
 * finishes it; the form only receives a value once all three parts make a real date. */
export function EthDatePicker({ id, value, todayEth, onChange, span = 110 }: Props) {
  const [parts, setParts] = useState({
    y: value?.year ?? 0,
    m: value?.month ?? 0,
    d: value?.day ?? 0,
  });
  useEffect(() => {
    if (value) setParts({ y: value.year, m: value.month, d: value.day });
  }, [value]);
  const { y, m, d } = parts;
  const years = Array.from({ length: span + 1 }, (_, i) => todayEth.year - i);
  const maxDay = m ? ethDaysInMonth(y || todayEth.year, m) : 30;
  const set = (ny: number, nm: number, nd: number) => {
    const fixedDay = ny && nm && nd ? Math.min(nd, ethDaysInMonth(ny, nm)) : nd;
    setParts({ y: ny, m: nm, d: fixedDay });
    onChange(ny && nm && fixedDay ? { year: ny, month: nm, day: fixedDay } : null);
  };
  return (
    <div class="ecdate" id={id}>
      <select
        aria-label="Day"
        value={d || ''}
        onChange={(e) => set(y, m, Number(e.currentTarget.value))}
      >
        <option value="">Day</option>
        {Array.from({ length: maxDay }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      <select
        aria-label="Month"
        value={m || ''}
        onChange={(e) => set(y, Number(e.currentTarget.value), d)}
      >
        <option value="">Month</option>
        {ETH_MONTHS_EN.map((name, i) => (
          <option key={name} value={i + 1}>
            {name}
          </option>
        ))}
      </select>
      <select
        aria-label="Year"
        value={y || ''}
        onChange={(e) => set(Number(e.currentTarget.value), m, d)}
      >
        <option value="">Year</option>
        {years.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  );
}
